import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck,
  Loader2,
  LogOut,
  Activity,
  Eye,
  Users,
  Star,
  Stethoscope,
  RefreshCw,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "idiag-admin-token";

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-card/60 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="w-4 h-4 text-primary" />
        {label}
      </div>
      <div className="text-3xl font-extrabold mt-2">{value}</div>
      {sub != null && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function BarList({ title, items, color = "bg-primary" }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="rounded-2xl border border-border/70 bg-card/60 p-4">
      <h3 className="text-sm font-semibold mb-3">{title}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucune donnée.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.label}>
              <div className="flex items-center justify-between text-sm mb-0.5">
                <span className="truncate pr-2">{it.label}</span>
                <span className="font-mono text-muted-foreground">{it.count}</span>
              </div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div
                  className={`h-full ${color} rounded-full`}
                  style={{ width: `${(it.count / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY) || "";
    } catch {
      return "";
    }
  });
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchStats = useCallback(
    async (tok) => {
      const useToken = tok ?? token;
      if (!useToken) return;
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${BACKEND}/api/admin/stats`, {
          headers: { "X-Admin-Token": useToken },
        });
        if (res.status === 401) {
          setError("Mot de passe admin incorrect.");
          setAuthed(false);
          return;
        }
        if (res.status === 503) {
          setError("Le tableau de bord admin n'est pas configuré (ADMIN_TOKEN manquant).");
          setAuthed(false);
          return;
        }
        if (!res.ok) throw new Error("failed");
        const data = await res.json();
        setStats(data);
        setAuthed(true);
        try {
          localStorage.setItem(TOKEN_KEY, useToken);
        } catch {
          /* noop */
        }
        setToken(useToken);
        try {
          const logRes = await fetch(`${BACKEND}/api/admin/logs?lines=200`, {
            headers: { "X-Admin-Token": useToken },
          });
          if (logRes.ok) setLogs((await logRes.json()).lines || []);
        } catch {
          /* logs are best-effort */
        }
      } catch {
        setError("Impossible de charger les statistiques.");
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) fetchStats(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* noop */
    }
    setToken("");
    setAuthed(false);
    setStats(null);
    setInput("");
  };

  if (!authed) {
    return (
      <main className="max-w-md mx-auto px-5 pt-20 pb-24">
        <div className="rounded-3xl border border-border/70 bg-card/60 p-8 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
            <ShieldCheck className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-2xl font-extrabold">Espace administrateur</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">
            Accès réservé. Entrez le mot de passe admin.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              fetchStats(input.trim());
            }}
            className="space-y-3"
          >
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Mot de passe admin"
              data-testid="admin-token-input"
              className="w-full h-11 rounded-xl border border-input bg-transparent px-4 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="admin-login-button"
              className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-primary text-primary-foreground font-semibold hover:brightness-110 transition disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Se connecter
            </button>
            {error && (
              <p className="text-sm text-destructive" data-testid="admin-error">
                {error}
              </p>
            )}
          </form>
        </div>
      </main>
    );
  }

  const t = stats?.totals || {};

  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-24">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <Activity className="w-3.5 h-3.5" /> Tableau de bord
          </div>
          <h1 className="text-3xl font-extrabold" data-testid="admin-title">
            Utilisation de l'app
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchStats()}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-secondary text-sm font-medium hover:brightness-110 transition"
            data-testid="admin-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
          </button>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-secondary text-sm font-medium hover:brightness-110 transition"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard icon={Stethoscope} label="Diagnostics" value={t.diagnoses ?? 0} />
        <StatCard icon={Eye} label="Vues de pages" value={t.page_views ?? 0} />
        <StatCard icon={Users} label="Visiteurs uniques" value={t.unique_visitors ?? 0} />
        <StatCard icon={Star} label="Avis" value={t.reviews ?? 0} sub={`Note moy. ${t.avg_rating ?? 0}/5`} />
        <StatCard
          icon={Activity}
          label="Diag./visiteur"
          value={t.unique_visitors ? (t.diagnoses / t.unique_visitors).toFixed(1) : "0"}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <BarList title="Vues de pages par jour" items={stats?.views_by_day || []} />
        <BarList title="Diagnostics par jour" items={stats?.diagnoses_by_day || []} color="bg-fuchsia-500" />
      </div>

      <div className="mb-4 rounded-2xl border border-border/70 bg-card/60 p-4">
        <h3 className="text-sm font-semibold mb-3">
          Diagnostics récents ({(stats?.recent_diagnoses || []).length})
        </h3>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm" data-testid="admin-recent-diagnoses">
            <thead className="text-xs text-muted-foreground sticky top-0 bg-card">
              <tr className="text-left">
                <th className="py-1.5 pr-3">Prénom</th>
                <th className="py-1.5 pr-3">Appareil</th>
                <th className="py-1.5 pr-3">Type de panic</th>
                <th className="py-1.5 pr-3">Pays</th>
                <th className="py-1.5">Date</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.recent_diagnoses || []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-3 text-muted-foreground">
                    Aucun diagnostic pour le moment.
                  </td>
                </tr>
              ) : (
                (stats?.recent_diagnoses || []).map((d, i) => (
                  <tr key={i} className="border-t border-border/50">
                    <td className="py-1.5 pr-3 font-semibold">{d.name}</td>
                    <td className="py-1.5 pr-3">{d.device}</td>
                    <td className="py-1.5 pr-3 text-xs">{d.panic}</td>
                    <td className="py-1.5 pr-3 text-xs">{d.country}</td>
                    <td className="py-1.5 text-muted-foreground text-xs whitespace-nowrap">
                      {d.created_at ? new Date(d.created_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        <BarList title="Par prénom" items={stats?.by_name || []} color="bg-sky-500" />
        <BarList title="Par pays" items={stats?.by_country || []} color="bg-emerald-500" />
        <BarList title="Par modèle d'iPhone" items={stats?.by_device || []} />
        <BarList title="Par type de panic" items={stats?.by_panic_type || []} color="bg-fuchsia-500" />
        <BarList title="Par langue" items={stats?.by_lang || []} color="bg-amber-500" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <BarList title="Pages les plus vues" items={stats?.top_paths || []} />
        <div className="rounded-2xl border border-border/70 bg-card/60 p-4">
          <h3 className="text-sm font-semibold mb-3">
            Visiteurs ({(stats?.visitors || []).length})
          </h3>
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm" data-testid="admin-visitors">
              <thead className="text-xs text-muted-foreground sticky top-0 bg-card">
                <tr className="text-left">
                  <th className="py-1.5 pr-2">IP</th>
                  <th className="py-1.5 pr-2">Pays</th>
                  <th className="py-1.5 pr-2">Vues</th>
                  <th className="py-1.5">Dernière visite</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.visitors || []).map((v) => (
                  <tr key={v.visitor_id} className="border-t border-border/50">
                    <td className="py-1.5 pr-2 font-mono text-xs">{v.ip || "—"}</td>
                    <td className="py-1.5 pr-2 text-xs">{v.country || "—"}</td>
                    <td className="py-1.5 pr-2 font-mono">{v.views}</td>
                    <td className="py-1.5 text-muted-foreground text-xs">
                      {v.last_seen ? new Date(v.last_seen).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-border/70 bg-card/60 p-4">
        <h3 className="text-sm font-semibold mb-2">
          Journal des visites (fichier sur le VPS)
        </h3>
        <div
          className="max-h-72 overflow-auto rounded-lg bg-black/40 border border-border/50 p-3 font-mono text-[11px] leading-relaxed text-muted-foreground"
          data-testid="admin-logs"
        >
          {logs.length === 0 ? (
            <span>Aucune entrée de journal.</span>
          ) : (
            logs.map((line, i) => (
              <div key={i} className="whitespace-pre-wrap break-all">
                {line}
              </div>
            ))
          )}
        </div>
      </div>
    </main>
  );
}
